import { apiClient } from '@/shared/lib/axios';

/** Params for admin distance cache list. */
export interface DistanceCacheListParams {
  /** Filter by tenant site_type (array). Empty = all. */
  site_type?: string[];
  clinic_number?: string;
  address1?: string;
  address2?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

/** One row in admin distance cache list. */
export interface DistanceCacheListItem {
  id: number;
  clinic_id: number;
  clinic_number: string | null;
  site_type: string | null;
  distance: number | null;
  address1: string | null;
  latitude1: string | null;
  longitude1: string | null;
  address2: string | null;
  latitude2: string | null;
  longitude2: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface DistanceCacheListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface DistanceCacheListResponse {
  success: boolean;
  message?: string;
  data: {
    data: DistanceCacheListItem[];
    meta: DistanceCacheListMeta;
  };
}

export const getDistanceCaches = async (
  site: string,
  params: DistanceCacheListParams
): Promise<DistanceCacheListResponse> => {
  const response = await apiClient.get<DistanceCacheListResponse>(
    `/admin/${site}/distance-caches`,
    { params }
  );
  return response.data;
};

/**
 * Download distance caches as CSV.
 */
export const downloadDistanceCachesCsv = async (
  site: string,
  params: DistanceCacheListParams
): Promise<void> => {
  const { page: _page, per_page: _perPage, ...csvParams } = params;
  const response = await apiClient.get(`/admin/${site}/distance-caches/csv`, {
    params: csvParams,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=UTF-8;' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `distance_cache${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Detail response shape. */
export interface DistanceCacheDetailResponse {
  success: boolean;
  message?: string;
  data: DistanceCacheListItem;
}

/** Update payload. */
export interface DistanceCacheUpdatePayload {
  distance: number;
  address1: string;
  latitude1: string;
  longitude1: string;
  address2: string;
  latitude2: string;
  longitude2: string;
}

export const getDistanceCache = async (
  site: string,
  id: number
): Promise<DistanceCacheDetailResponse> => {
  const response = await apiClient.get<DistanceCacheDetailResponse>(
    `/admin/${site}/distance-caches/${id}`
  );
  return response.data;
};

export const updateDistanceCache = async (
  site: string,
  id: number,
  data: DistanceCacheUpdatePayload
): Promise<DistanceCacheDetailResponse> => {
  const response = await apiClient.put<DistanceCacheDetailResponse>(
    `/admin/${site}/distance-caches/${id}`,
    data
  );
  return response.data;
};

export const deleteDistanceCache = async (
  site: string,
  id: number
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.delete<{ success: boolean; message?: string }>(
    `/admin/${site}/distance-caches/${id}`
  );
  return response.data;
};
